import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Checkbox,
    CircularProgress,
    Alert,
    Radio,
    RadioGroup,
    FormControlLabel,
    Chip,
    Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { DesktopLayout } from 'src/layout';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import LeadStreamsService from 'src/services/LeadStreamsService';

import PaymentMethods from 'src/screens/Payment/components/PaymentMethods';
import Points from 'src/screens/Payment/components/Points';
import TicketField from 'src/components/TicketField';
import useUserStore from 'src/stores/user';
import formatNumber from 'src/utils/formatNumber';
import SubscriptionsService from 'src/services/SubscriptionsService';
import { AIIcon } from 'src/icons/AIIcon';
import { updateBeginWorkStep } from 'src/providers/UpdateBeginWorkStepsProvider';
import { BEGIN_WORK_STEPS_IDS } from 'src/constants/beginWorksSteps';

import type { ActivePackagePromotion, LeadCategory, LeadPackagePrice } from 'src/services/LeadStreamsService/agent';

const CustomTextField = styled(TextField)(({ theme }) => ({
    '&.MuiTextField-root': { borderRadius: '16px' },
    backgroundColor: '#fff',
    '& .MuiInputBase-root:not(.Mui-focused) .MuiInputBase-input:not(:placeholder-shown)': {
        color: theme.palette.primary.main,
        opacity: 1,
    },
    '& .MuiOutlinedInput-root': {
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
    },
}));

const MaxChars = ({ isError, charCount, maxChars }: { isError: boolean; charCount: number; maxChars: number }) => {
    return (
        <Stack
            gap="2px"
            direction="row"
            alignItems="center"
            sx={{ mt: 0.5 }}
        >
            {!isError && <AIIcon />}

            <Typography
                variant="body2"
                sx={{
                    background: isError ? 'none' : 'linear-gradient(90deg, #FF88C0, #9FA9BC)',
                    WebkitBackgroundClip: isError ? 'none' : 'text',
                    color: isError ? 'error.main' : 'transparent',
                }}
            >
                {isError ? `Превышено количество символов (максимум ${maxChars})` : `${charCount}/${maxChars}`}
            </Typography>
        </Stack>
    );
};

interface SelectedCategory {
    category: LeadCategory;
    packageSize: number | null;
    prices: LeadPackagePrice[];
}

const normalizePromotionMultiplier = (value: number | string | null | undefined): number => {
    const numeric = typeof value === 'string' ? Number(value) : value ?? 0;
    return Number.isFinite(numeric) && numeric > 1 ? numeric : 1;
};

const getCreditedLeadCount = (packageSize: number, promotion?: ActivePackagePromotion | null): number => {
    const normalizedSize = Math.max(0, Math.trunc(packageSize || 0));
    if (normalizedSize <= 0) {
        return 0;
    }
    return Math.max(0, Math.round(normalizedSize * normalizePromotionMultiplier(promotion?.multiplier)));
};

const getPromotionBadgeLabel = (promotion?: ActivePackagePromotion | null): string => {
    if (!promotion) {
        return 'Акция';
    }
    return (
        promotion.badge_text ||
        promotion.multiplier_label ||
        `x${String(normalizePromotionMultiplier(promotion.multiplier)).replace(/\.0+$/, '')}`
    );
};

type LeadStreamForm = {
    paymentMethod: string;
    bonus: boolean;
    promo_code?: string;
    ai_prompt?: string;
};

export default function LeadStreamScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const isMobile = useIsMobileSize();
    const { bonusPoints } = useUserStore(({ bonusPoints }) => ({ bonusPoints }));
    const onboardingStepName = useUserStore((state) => state.onboardingStepName);

    // При открытии страницы leadstream обновляем шаг на choose_stream, если текущий шаг не choose_stream
    // Это позволяет пользователю переключиться с пути мониторинга на путь потоков лидов
    useEffect(() => {
        if (onboardingStepName && onboardingStepName !== BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM) {
            console.log(
                `🔄 [LeadStream] Обновляем шаг с ${onboardingStepName} на choose_stream для доступа к потокам лидов`
            );
            updateBeginWorkStep({
                currentStep: BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM,
                onSuccess: () => {
                    console.log(`✅ [LeadStream] Шаг успешно обновлен на choose_stream`);
                },
            });
        }
    }, [onboardingStepName]);

    const STORAGE_KEY = 'leadstream_selection';

    // Загрузка сохраненного выбора из localStorage
    const loadSavedSelection = (): {
        selectedCategories: Map<number, SelectedCategory>;
        formData: Partial<LeadStreamForm>;
    } => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                const categoriesMap = new Map<number, SelectedCategory>();

                if (parsed.selectedCategories && Array.isArray(parsed.selectedCategories)) {
                    parsed.selectedCategories.forEach((item: any) => {
                        if (item.category && item.category.id) {
                            categoriesMap.set(item.category.id, {
                                category: item.category,
                                packageSize: item.packageSize || null,
                                prices: item.prices || [],
                            });
                        }
                    });
                }

                return {
                    selectedCategories: categoriesMap,
                    formData: parsed.formData || {},
                };
            }
        } catch (error) {
            console.error('Ошибка загрузки сохраненного выбора:', error);
        }

        return {
            selectedCategories: new Map(),
            formData: {},
        };
    };

    const [categories, setCategories] = useState<LeadCategory[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const savedSelection = loadSavedSelection();
    const [selectedCategories, setSelectedCategories] = useState<Map<number, SelectedCategory>>(
        savedSelection.selectedCategories
    );
    const [categoryPrices, setCategoryPrices] = useState<Map<number, LeadPackagePrice[]>>(new Map());
    const [loadingPrices, setLoadingPrices] = useState<Set<number>>(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [promoDiscount, setPromoDiscount] = useState<number | null>(null);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);

    const { control, handleSubmit, watch, setValue } = useForm<LeadStreamForm>({
        defaultValues: {
            paymentMethod: 'UMoney',
            bonus: false,
            promo_code: savedSelection.formData.promo_code || '',
            ai_prompt: savedSelection.formData.ai_prompt || '',
        },
    });

    // Сохранение выбора в localStorage
    const saveSelection = (categories: Map<number, SelectedCategory>, formData: Partial<LeadStreamForm>) => {
        try {
            const categoriesArray = Array.from(categories.values()).map((selected) => ({
                category: selected.category,
                packageSize: selected.packageSize,
                prices: selected.prices,
            }));

            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    selectedCategories: categoriesArray,
                    formData: {
                        promo_code: formData.promo_code || '',
                        ai_prompt: formData.ai_prompt || '',
                    },
                })
            );
        } catch (error) {
            console.error('Ошибка сохранения выбора:', error);
        }
    };

    // Загрузка категорий с фильтрацией
    useEffect(() => {
        setCategoriesLoading(true);
        setCategoriesError(null);
        LeadStreamsService.getCategories()
            .then((data) => {
                // Фильтруем только активные и доступные для пакетов категории
                const filtered = data.filter((cat) => cat.is_active && cat.is_available_for_packages);
                setCategories(filtered);
                setCategoriesLoading(false);
            })
            .catch((error) => {
                console.error('Ошибка загрузки категорий:', error);
                const errorMessage =
                    error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    'Не удалось загрузить категории. Попробуйте обновить страницу.';
                setCategoriesError(errorMessage);
                enqueueSnackbar(errorMessage, { variant: 'error' });
                setCategoriesLoading(false);
            });
    }, [enqueueSnackbar]);

    // Загрузка цен для выбранных категорий
    useEffect(() => {
        selectedCategories.forEach((selected, categoryId) => {
            if (!categoryPrices.has(categoryId) && !loadingPrices.has(categoryId)) {
                setLoadingPrices((prev) => new Set(prev).add(categoryId));
                LeadStreamsService.getCategoryPrices(categoryId)
                    .then((prices) => {
                        // Фильтруем только активные цены
                        const activePrices = prices.filter((p) => p.is_active);
                        setCategoryPrices((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(categoryId, activePrices);
                            return newMap;
                        });

                        const recommendedPackageSize = selected.category.package_recommended_size || null;
                        const selectedPackageExists = selected.packageSize
                            ? activePrices.some((p) => p.package_size === selected.packageSize)
                            : false;
                        const recommendedPrice = recommendedPackageSize
                            ? activePrices.find((p) => p.package_size === recommendedPackageSize)
                            : null;

                        // Если текущий выбор уже валиден, сохраняем его.
                        if (selectedPackageExists) {
                            setLoadingPrices((prev) => {
                                const newSet = new Set(prev);
                                newSet.delete(categoryId);
                                return newSet;
                            });
                            return;
                        }

                        // Иначе сначала используем рекомендованный старт для ниши.
                        if (recommendedPrice) {
                            setSelectedCategories((prev) => {
                                const newMap = new Map(prev);
                                const selectedCat = newMap.get(categoryId);
                                if (selectedCat) {
                                    newMap.set(categoryId, {
                                        ...selectedCat,
                                        packageSize: recommendedPrice.package_size,
                                    });
                                }
                                return newMap;
                            });
                        } else {
                            // Если рекомендованный не найден, используем привычный пакет 100 лидов.
                            const has100Package = activePrices.some((p) => p.package_size === 100);
                            if (has100Package) {
                                setSelectedCategories((prev) => {
                                    const newMap = new Map(prev);
                                    const selectedCat = newMap.get(categoryId);
                                    if (selectedCat) {
                                        newMap.set(categoryId, {
                                            ...selectedCat,
                                            packageSize: 100,
                                        });
                                    }
                                    return newMap;
                                });
                            } else if (activePrices.length > 0) {
                                // Иначе выбираем минимальный доступный пакет.
                                const minPackage = activePrices.reduce((min, p) =>
                                    p.package_size < min.package_size ? p : min
                                );
                                setSelectedCategories((prev) => {
                                    const newMap = new Map(prev);
                                    const selectedCat = newMap.get(categoryId);
                                    if (selectedCat) {
                                        newMap.set(categoryId, {
                                            ...selectedCat,
                                            packageSize: minPackage.package_size,
                                        });
                                    }
                                    return newMap;
                                });
                            }
                        }

                        setLoadingPrices((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(categoryId);
                            return newSet;
                        });
                    })
                    .catch((error) => {
                        console.error(`Ошибка загрузки цен для категории ${categoryId}:`, error);
                        enqueueSnackbar(`Не удалось загрузить цены для категории "${selected.category.name}"`, {
                            variant: 'error',
                        });
                        setLoadingPrices((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(categoryId);
                            return newSet;
                        });
                    });
            }
        });
    }, [selectedCategories, categoryPrices, loadingPrices, enqueueSnackbar]);

    // Фильтрация категорий по поисковому запросу
    const filteredCategories = useMemo(() => {
        if (!searchQuery.trim()) return categories;
        const query = searchQuery.toLowerCase();
        return categories.filter(
            (cat) =>
                cat.name.toLowerCase().includes(query) ||
                (cat.description && cat.description.toLowerCase().includes(query))
        );
    }, [categories, searchQuery]);

    // Переключение выбора категории
    const toggleCategory = (category: LeadCategory) => {
        setSelectedCategories((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(category.id)) {
                newMap.delete(category.id);
                setCategoryPrices((prev) => {
                    const newMap = new Map(prev);
                    newMap.delete(category.id);
                    return newMap;
                });
            } else {
                newMap.set(category.id, {
                    category,
                    packageSize: category.package_recommended_size || 100,
                    prices: [],
                });
            }
            // Сохраняем выбор
            setTimeout(() => {
                saveSelection(newMap, {
                    promo_code: watch('promo_code'),
                    ai_prompt: watch('ai_prompt'),
                });
            }, 0);
            return newMap;
        });
    };

    // Выбор пакета для категории
    const selectPackage = (categoryId: number, packageSize: number) => {
        setSelectedCategories((prev) => {
            const newMap = new Map(prev);
            const selected = newMap.get(categoryId);
            if (selected) {
                newMap.set(categoryId, {
                    ...selected,
                    packageSize,
                });
            }
            // Сохраняем выбор
            setTimeout(() => {
                saveSelection(newMap, {
                    promo_code: watch('promo_code'),
                    ai_prompt: watch('ai_prompt'),
                });
            }, 0);
            return newMap;
        });
    };

    // Расчет базовой цены (цена минимального пакета)
    const getBasePrice = (prices: LeadPackagePrice[]): number => {
        if (!prices.length) return 0;
        const minPackage = prices.reduce((min, p) => (p.package_size < min.package_size ? p : min));
        return parseFloat(minPackage.price_per_lead);
    };

    // Расчет скидки и экономии
    const calculateDiscount = (basePrice: number, currentPrice: number, packageSize: number) => {
        if (basePrice === 0) return { discount: 0, savings: 0 };
        const baseTotal = basePrice * packageSize;
        const currentTotal = currentPrice * packageSize;
        const discount = ((basePrice - currentPrice) / basePrice) * 100;
        const savings = baseTotal - currentTotal;
        return { discount: Math.max(0, discount), savings: Math.max(0, savings) };
    };

    // Расчет общей выгоды
    const calculateTotalSavings = (): number => {
        let totalSavings = 0;
        selectedCategories.forEach((selected) => {
            if (selected.packageSize && categoryPrices.has(selected.category.id)) {
                const prices = categoryPrices.get(selected.category.id)!;
                const basePrice = getBasePrice(prices);
                const priceObj = prices.find((p) => p.package_size === selected.packageSize);
                if (priceObj) {
                    const pricePerLead = parseFloat(priceObj.price_per_lead);
                    const { savings } = calculateDiscount(basePrice, pricePerLead, selected.packageSize);
                    totalSavings += savings;
                }
            }
        });
        return totalSavings;
    };

    // Расчет итоговой суммы
    const calculateTotal = (): number => {
        let total = 0;
        selectedCategories.forEach((selected) => {
            if (selected.packageSize && categoryPrices.has(selected.category.id)) {
                const prices = categoryPrices.get(selected.category.id)!;
                const priceObj = prices.find((p) => p.package_size === selected.packageSize);
                if (priceObj) {
                    total += parseFloat(priceObj.price_per_lead) * selected.packageSize;
                }
            }
        });
        return total;
    };

    const total = calculateTotal();
    const totalSavings = calculateTotalSavings();
    const isUseBonus = watch('bonus');
    const promoCode = watch('promo_code');
    const actualBonusToUse = isUseBonus && bonusPoints > 0 ? Math.min(bonusPoints, total) : 0;

    // Расчет скидки по промокоду
    const promoDiscountAmount = promoDiscount && total > 0 ? (total * promoDiscount) / 100 : 0;
    const totalAfterPromo = total - promoDiscountAmount;
    const finalTotal = Math.max(0, totalAfterPromo - actualBonusToUse);

    // Валидация промокода при изменении
    useEffect(() => {
        const validatePromo = async () => {
            if (!promoCode || !promoCode.trim()) {
                setPromoDiscount(null);
                setPromoError(null);
                return;
            }

            setIsValidatingPromo(true);
            setPromoError(null);
            try {
                const response = await SubscriptionsService.checkPromocode(promoCode.trim());
                if (response && response.discount_percent) {
                    setPromoDiscount(response.discount_percent);
                } else {
                    setPromoDiscount(null);
                    setPromoError('Недействительный промокод');
                }
            } catch (error: any) {
                setPromoDiscount(null);
                setPromoError(error?.error || 'Недействительный промокод');
            } finally {
                setIsValidatingPromo(false);
            }
        };

        const timeoutId = setTimeout(validatePromo, 500); // Debounce
        return () => clearTimeout(timeoutId);
    }, [promoCode]);

    // Сохранение выбора при изменении формы
    const aiPrompt = watch('ai_prompt');
    useEffect(() => {
        saveSelection(selectedCategories, {
            promo_code: promoCode,
            ai_prompt: aiPrompt,
        });
    }, [promoCode, aiPrompt, selectedCategories]);

    const onSubmit = async (data: LeadStreamForm) => {
        // Валидация: все категории должны иметь выбранный пакет
        const categoriesWithoutPackage = Array.from(selectedCategories.values()).filter((s) => !s.packageSize);
        if (categoriesWithoutPackage.length > 0) {
            enqueueSnackbar('Выберите пакет для всех категорий', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);

        try {
            const aiPrompt = data.ai_prompt?.trim() || undefined;

            // Создаем stream'ы и покупки для каждой категории
            const purchasePromises = Array.from(selectedCategories.values()).map(async (selected) => {
                // Создаем или получаем stream для категории
                // Бэкенд автоматически вернет существующий stream, если он уже есть
                const stream = await LeadStreamsService.createStream({
                    category_id: selected.category.id,
                    prompt: aiPrompt,
                });

                // Создаем покупку
                const purchaseData: any = {
                    package_size: selected.packageSize!,
                };

                // Добавляем промокод, если он валиден
                if (promoCode && promoCode.trim() && promoDiscount) {
                    purchaseData.promo_code = promoCode.trim();
                }

                // Добавляем информацию об использовании бонусов
                if (isUseBonus) {
                    purchaseData.use_bonus_points = true;
                }

                const purchaseResponse = await LeadStreamsService.purchasePackage(stream.id, purchaseData);

                return purchaseResponse;
            });

            const purchaseResponses = await Promise.all(purchasePromises);

            // Очищаем сохраненный выбор после успешной покупки
            localStorage.removeItem(STORAGE_KEY);

            // Если все покупки успешны, перенаправляем на первую ссылку оплаты
            // В будущем можно объединить все покупки в один платеж
            if (purchaseResponses.length > 0 && purchaseResponses[0].confirmation_url) {
                window.location.href = purchaseResponses[0].confirmation_url;
            } else {
                enqueueSnackbar('Покупки созданы, но ссылка на оплату не получена', {
                    variant: 'warning',
                });
            }
        } catch (error: any) {
            console.error('Ошибка создания покупок:', error);
            const errorMessage =
                error?.message ||
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Не удалось создать покупки. Попробуйте позже.';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderContent = () => {
        return (
            <Box
                display="flex"
                flexDirection="column"
                minHeight="100%"
                flexGrow={1}
            >
                <Box mb={3}>
                    <Typography
                        variant="h1"
                        color="primary"
                    >
                        Запуск потока лидов
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 0, 0, 0.7)',
                            fontSize: 16,
                            fontWeight: 400,
                            mt: 1.5,
                            lineHeight: 1.5,
                            maxWidth: '800px',
                        }}
                    >
                        Выберите потоки лидов нужной вам категории и получайте готовые лиды 24/7
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Поиск категорий */}
                    <Box mb={3}>
                        <TextField
                            fullWidth
                            placeholder="Поиск категорий..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography>🔍</Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '16px',
                                    backgroundColor: '#fff',
                                },
                            }}
                        />
                    </Box>

                    {/* Ошибка загрузки категорий */}
                    {categoriesError && (
                        <Box mb={3}>
                            <Alert
                                severity="error"
                                onClose={() => setCategoriesError(null)}
                            >
                                {categoriesError}
                            </Alert>
                        </Box>
                    )}

                    {/* Список категорий */}
                    {categoriesLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            p={4}
                        >
                            <CircularProgress />
                        </Box>
                    ) : filteredCategories.length === 0 ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            p={4}
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '16px',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                            }}
                        >
                            <Typography
                                variant="h6"
                                color="text.secondary"
                                mb={1}
                            >
                                {searchQuery.trim() ? 'Категории не найдены' : 'Нет доступных категорий'}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {searchQuery.trim()
                                    ? 'Попробуйте изменить поисковый запрос'
                                    : 'Категории будут добавлены позже'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: '500px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                mb: 4,
                                pr: 1,
                                py: 2,
                                '&::-webkit-scrollbar': {
                                    width: 8,
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: 4,
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: 4,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    },
                                },
                            }}
                        >
                            <Box
                                display="grid"
                                gridTemplateColumns={isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))'}
                                gap={2}
                            >
                                {filteredCategories.map((category) => {
                                    const isSelected = selectedCategories.has(category.id);
                                    const categoryColor = category.color || '#0044FF';
                                    return (
                                        <Card
                                            key={category.id}
                                            onClick={() => toggleCategory(category)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: '16px',
                                                border: `2px solid ${
                                                    isSelected ? categoryColor : 'rgba(0, 0, 0, 0.12)'
                                                }`,
                                                boxShadow: isSelected
                                                    ? `0 4px 16px ${categoryColor}40`
                                                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                transition: 'all 0.3s ease',
                                                backgroundColor: isSelected ? `${categoryColor}08` : '#fff',
                                                '&:hover': {
                                                    boxShadow: `0 4px 16px ${categoryColor}40`,
                                                    transform: 'translateY(-4px)',
                                                },
                                                '&:active': {
                                                    transform: 'translateY(-2px)',
                                                },
                                            }}
                                        >
                                            <CardContent>
                                                <Box
                                                    display="flex"
                                                    alignItems="flex-start"
                                                    gap={1.5}
                                                    mb={1}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            toggleCategory(category);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            mt: -1,
                                                            ml: -1,
                                                            color: categoryColor,
                                                            '&.Mui-checked': {
                                                                color: categoryColor,
                                                            },
                                                        }}
                                                    />
                                                    <Box
                                                        flex={1}
                                                        minWidth={0}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1.5}
                                                            mb={0.5}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 20,
                                                                    flexShrink: 0,
                                                                    lineHeight: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                {category.icon || '📁'}
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    flex: 1,
                                                                    fontSize: 16,
                                                                    lineHeight: 1.4,
                                                                }}
                                                            >
                                                                {category.name}
                                                            </Typography>
                                                        </Box>
                                                        {category.description && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'rgba(0, 0, 0, 0.7)',
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {category.description}
                                                            </Typography>
                                                        )}
                                                        {category.availability_hint && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'rgba(0, 0, 0, 0.5)',
                                                                    mt: 0.5,
                                                                    display: 'block',
                                                                }}
                                                            >
                                                                {category.availability_hint}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Выбранные категории с пакетами */}
                    <Box mb={4}>
                        <Typography
                            variant="h5"
                            color="primary"
                            sx={{
                                fontWeight: 800,
                                fontSize: 24,
                                mb: 2.5,
                                lineHeight: 1.2,
                            }}
                        >
                            Выбранные категории
                        </Typography>

                        {selectedCategories.size === 0 ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                p={6}
                            >
                                <Typography sx={{ fontSize: 48, mb: 2 }}>⚡</Typography>
                                <Typography
                                    variant="h6"
                                    color="text.primary"
                                    mb={1}
                                    textAlign="center"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Выберите категории
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                    textAlign="center"
                                    sx={{ maxWidth: '400px' }}
                                >
                                    Выберите нужные категории из предложенных вариантов сверху, чтобы увидеть доступные
                                    пакеты лидов
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {Array.from(selectedCategories.values()).map((selected) => {
                                    const prices = categoryPrices.get(selected.category.id) || [];
                                    const basePrice = getBasePrice(prices);
                                    const isLoading = loadingPrices.has(selected.category.id);
                                    const activePackagePromotion = selected.category.active_package_promotion || null;

                                    return (
                                        <Card
                                            key={selected.category.id}
                                            sx={{
                                                mb: 2,
                                                borderRadius: '16px',
                                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                                backgroundColor: '#fff',
                                            }}
                                        >
                                            <CardContent>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1.5}
                                                    mb={2}
                                                >
                                                    <Typography sx={{ fontSize: 24 }}>
                                                        {selected.category.icon || '📁'}
                                                    </Typography>
                                                    <Box flex={1}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            {selected.category.name}
                                                        </Typography>
                                                        {activePackagePromotion ? (
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                alignItems="center"
                                                                flexWrap="wrap"
                                                                useFlexGap
                                                                sx={{ mt: 0.75 }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    color="success"
                                                                    label={getPromotionBadgeLabel(
                                                                        activePackagePromotion
                                                                    )}
                                                                    sx={{ fontWeight: 700 }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: 'success.main', fontWeight: 600 }}
                                                                >
                                                                    К любой покупке добавим лиды по акции.
                                                                </Typography>
                                                            </Stack>
                                                        ) : null}
                                                        {selected.category.description && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'rgba(0, 0, 0, 0.7)',
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {selected.category.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Button
                                                        size="small"
                                                        onClick={() => toggleCategory(selected.category)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        Убрать
                                                    </Button>
                                                </Box>

                                                {isLoading ? (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        p={2}
                                                    >
                                                        <CircularProgress size={24} />
                                                    </Box>
                                                ) : prices.length > 0 ? (
                                                    <Box
                                                        sx={{
                                                            overflowX: 'auto',
                                                            overflowY: 'hidden',
                                                            py: 1,
                                                            '&::-webkit-scrollbar': {
                                                                height: 6,
                                                            },
                                                            '&::-webkit-scrollbar-track': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                                borderRadius: 3,
                                                            },
                                                            '&::-webkit-scrollbar-thumb': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                                                borderRadius: 3,
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <RadioGroup
                                                            value={selected.packageSize?.toString() || ''}
                                                            onChange={(e) =>
                                                                selectPackage(
                                                                    selected.category.id,
                                                                    parseInt(e.target.value)
                                                                )
                                                            }
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                gap: 1.5,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            {prices.map((price) => {
                                                                const pricePerLead = parseFloat(price.price_per_lead);
                                                                const { discount, savings } = calculateDiscount(
                                                                    basePrice,
                                                                    pricePerLead,
                                                                    price.package_size
                                                                );
                                                                const creditedLeadCount = getCreditedLeadCount(
                                                                    price.package_size,
                                                                    activePackagePromotion
                                                                );
                                                                const hasPromotion =
                                                                    creditedLeadCount > price.package_size;
                                                                const effectivePromoLeadPrice =
                                                                    hasPromotion && creditedLeadCount > 0
                                                                        ? (pricePerLead * price.package_size) /
                                                                          creditedLeadCount
                                                                        : null;
                                                                const isSelected =
                                                                    selected.packageSize === price.package_size;
                                                                const categoryColor =
                                                                    selected.category.color || '#0044FF';

                                                                return (
                                                                    <FormControlLabel
                                                                        key={price.id}
                                                                        value={price.package_size.toString()}
                                                                        control={
                                                                            <Radio
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    selectPackage(
                                                                                        selected.category.id,
                                                                                        price.package_size
                                                                                    );
                                                                                }}
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: 8,
                                                                                    right: 8,
                                                                                    color: categoryColor,
                                                                                    '&.Mui-checked': {
                                                                                        color: categoryColor,
                                                                                    },
                                                                                    zIndex: 1,
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Card
                                                                                onClick={() =>
                                                                                    selectPackage(
                                                                                        selected.category.id,
                                                                                        price.package_size
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    width: 180,
                                                                                    minWidth: 180,
                                                                                    height: 140,
                                                                                    borderRadius: '12px',
                                                                                    border: isSelected
                                                                                        ? `2px solid ${categoryColor}`
                                                                                        : '1px solid rgba(0, 0, 0, 0.12)',
                                                                                    backgroundColor: isSelected
                                                                                        ? `${categoryColor}08`
                                                                                        : '#fff',
                                                                                    boxShadow: isSelected
                                                                                        ? `0 2px 8px ${categoryColor}40`
                                                                                        : '0 1px 4px rgba(0, 0, 0, 0.1)',
                                                                                    transition: 'all 0.2s ease',
                                                                                    cursor: 'pointer',
                                                                                    position: 'relative',
                                                                                    '&:hover': {
                                                                                        boxShadow: `0 4px 12px ${categoryColor}40`,
                                                                                        transform: 'translateY(-2px)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                    <CardContent
                                                                                        sx={{
                                                                                            p: 2,
                                                                                            '&:last-child': { pb: 2 },
                                                                                            height: '100%',
                                                                                            display: 'flex',
                                                                                            flexDirection: 'column',
                                                                                            justifyContent: 'space-between',
                                                                                        }}
                                                                                    >
                                                                                        <Box>
                                                                                            {hasPromotion ? (
                                                                                                <Chip
                                                                                                    size="small"
                                                                                                    color="success"
                                                                                                    label={getPromotionBadgeLabel(
                                                                                                        activePackagePromotion
                                                                                                    )}
                                                                                                    sx={{
                                                                                                        mb: 1,
                                                                                                        fontWeight: 700,
                                                                                                    }}
                                                                                                />
                                                                                            ) : null}
                                                                                            <Typography
                                                                                                variant="body1"
                                                                                                sx={{
                                                                                                    fontWeight: 600,
                                                                                                    mb: 0.5,
                                                                                            }}
                                                                                        >
                                                                                            {price.package_size} лидов
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                color: 'rgba(0, 0, 0, 0.7)',
                                                                                                mb: 1,
                                                                                            }}
                                                                                                >
                                                                                                    {formatNumber(pricePerLead)}{' '}
                                                                                                    ₽ за лид
                                                                                            {discount > 0 && (
                                                                                                <Typography
                                                                                                    component="span"
                                                                                                    sx={{
                                                                                                        color: 'rgba(0, 0, 0, 0.5)',
                                                                                                        fontWeight: 400,
                                                                                                        fontSize: 14,
                                                                                                        textDecoration:
                                                                                                            'line-through',
                                                                                                        ml: 1,
                                                                                                    }}
                                                                                                >
                                                                                                    {formatNumber(
                                                                                                        basePrice
                                                                                                    )}{' '}
                                                                                                    ₽
                                                                                                </Typography>
                                                                                            )}
                                                                                        </Typography>
                                                                                            <Typography
                                                                                                variant="h6"
                                                                                                sx={{
                                                                                                    fontWeight: 700,
                                                                                                    mb: 0.5,
                                                                                                }}
                                                                                            >
                                                                                                {formatNumber(
                                                                                                    pricePerLead *
                                                                                                        price.package_size
                                                                                                )}{' '}
                                                                                                ₽
                                                                                            </Typography>
                                                                                            {hasPromotion ? (
                                                                                                <>
                                                                                                    <Typography
                                                                                                        variant="caption"
                                                                                                        sx={{
                                                                                                            color: '#22A55B',
                                                                                                            display: 'block',
                                                                                                            fontWeight: 700,
                                                                                                            mb: 0.25,
                                                                                                        }}
                                                                                                    >
                                                                                                        Начислим{' '}
                                                                                                        {creditedLeadCount}{' '}
                                                                                                        лидов вместо{' '}
                                                                                                        {price.package_size}
                                                                                                    </Typography>
                                                                                                    {effectivePromoLeadPrice !==
                                                                                                    null ? (
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            sx={{
                                                                                                                color: 'rgba(0, 0, 0, 0.7)',
                                                                                                                display: 'block',
                                                                                                            }}
                                                                                                        >
                                                                                                            По акции
                                                                                                            фактически{' '}
                                                                                                            {formatNumber(
                                                                                                                effectivePromoLeadPrice
                                                                                                            )}{' '}
                                                                                                            ₽ за лид
                                                                                                        </Typography>
                                                                                                    ) : null}
                                                                                                </>
                                                                                            ) : null}
                                                                                        </Box>
                                                                                        {discount > 0 && (
                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: '#4AE287',
                                                                                                    display: 'block',
                                                                                                    fontWeight: 600,
                                                                                                }}
                                                                                            >
                                                                                                Выгода:{' '}
                                                                                                {formatNumber(savings)}{' '}
                                                                                                ₽
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    )}
                                                                                </CardContent>
                                                                            </Card>
                                                                        }
                                                                        sx={{ margin: 0, position: 'relative' }}
                                                                    />
                                                                );
                                                            })}
                                                        </RadioGroup>
                                                    </Box>
                                                ) : (
                                                    <Alert severity="warning">
                                                        Нет доступных пакетов для этой категории
                                                    </Alert>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </>
                        )}
                    </Box>

                    {/* ИИ фильтр лидов */}
                    {selectedCategories.size > 0 && (
                        <>
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 1.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    ИИ фильтр лидов
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(0, 0, 0, 0.7)',
                                        mb: 2.5,
                                    }}
                                >
                                    Введите данные про свои услуги/продукт, чтобы сервис доставлял вам только валидные
                                    для вашего бизнеса лиды
                                </Typography>
                                <Stack gap={1}>
                                    <Typography
                                        component="label"
                                        htmlFor="ai_prompt"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Опишите ваш продукт или услугу
                                    </Typography>
                                    <Controller
                                        name="ai_prompt"
                                        control={control}
                                        render={({ field }) => {
                                            const charCount = (field.value || '').length;
                                            const isError = charCount > 150;

                                            return (
                                                <>
                                                    <CustomTextField
                                                        {...field}
                                                        id="ai_prompt"
                                                        placeholder="Опишите максимально подробно для лучшего результата работы ТГХ"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        error={isError}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '134px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            },
                                                        }}
                                                    />

                                                    <MaxChars
                                                        isError={isError}
                                                        charCount={charCount}
                                                        maxChars={150}
                                                    />
                                                </>
                                            );
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </>
                    )}

                    {/* Настройки оплаты */}
                    {selectedCategories.size > 0 && (
                        <>
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Настройки оплаты
                                </Typography>

                                <Box mb={2.5}>
                                    <PaymentMethods
                                        name="paymentMethod"
                                        control={control}
                                        methods={[{ name: 'ЮMoney', value: 'UMoney', currency: 'RUB' }]}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <Points
                                        name="bonus"
                                        control={control}
                                        points={bonusPoints}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <TicketField
                                        name="promo_code"
                                        control={control}
                                        placeholder="Промокод"
                                    />
                                    {isValidatingPromo && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'rgba(0, 0, 0, 0.5)', mt: 0.5, display: 'block' }}
                                        >
                                            Проверка промокода...
                                        </Typography>
                                    )}
                                    {promoError && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
                                        >
                                            {promoError}
                                        </Typography>
                                    )}
                                    {promoDiscount && !promoError && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#4AE287', mt: 0.5, display: 'block', fontWeight: 600 }}
                                        >
                                            Промокод применен! Скидка {promoDiscount}%
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Итоговая сумма */}
                            <Box mb={3}>
                                <Box
                                    border="1px solid #E3ECF3"
                                    bgcolor="#fff"
                                    borderRadius="16px"
                                    sx={{ overflow: 'hidden' }}
                                >
                                    {/* Список покупок */}
                                    <Box
                                        px={2}
                                        py={2}
                                        sx={{ borderBottom: '1px solid #E3ECF3' }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, mb: 1.5, color: 'rgba(0, 0, 0, 0.7)' }}
                                        >
                                            Вы выбрали:
                                        </Typography>
                                        {Array.from(selectedCategories.values()).map((selected) => {
                                            if (!selected.packageSize) return null;
                                            const prices = categoryPrices.get(selected.category.id) || [];
                                            const priceObj = prices.find(
                                                (p) => p.package_size === selected.packageSize
                                            );
                                            if (!priceObj) return null;

                                            const pricePerLead = parseFloat(priceObj.price_per_lead);
                                            const basePrice = getBasePrice(prices);
                                            const { discount } = calculateDiscount(
                                                basePrice,
                                                pricePerLead,
                                                selected.packageSize
                                            );
                                            const finalPricePerLead = pricePerLead;
                                            const totalForCategory = pricePerLead * selected.packageSize;

                                            return (
                                                <Box
                                                    key={selected.category.id}
                                                    mb={1.5}
                                                >
                                                    <Box
                                                        display="flex"
                                                        alignItems="flex-start"
                                                        justifyContent="space-between"
                                                        mb={0.5}
                                                    >
                                                        <Box flex={1}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                {selected.packageSize} лидов в &quot;
                                                                {selected.category.icon || '📁'}{' '}
                                                                {selected.category.name}&quot;
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 600, ml: 2 }}
                                                        >
                                                            {formatNumber(Math.round(totalForCategory))} ₽
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block' }}
                                                    >
                                                        {formatNumber(finalPricePerLead)} ₽ за лид
                                                        {discount > 0 && ` (скидка ${discount.toFixed(0)}%)`}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    {totalSavings > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                            sx={{ backgroundColor: 'rgba(74, 226, 135, 0.08)' }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#4AE287', fontWeight: 600 }}
                                            >
                                                Общая выгода
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#4AE287', fontWeight: 600 }}
                                            >
                                                {formatNumber(Math.round(totalSavings))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {promoDiscountAmount > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                            sx={{ backgroundColor: 'rgba(74, 226, 135, 0.08)' }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#4AE287', fontWeight: 600 }}
                                            >
                                                Скидка по промокоду ({promoDiscount}%)
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#4AE287', fontWeight: 600 }}
                                            >
                                                -{formatNumber(Math.round(promoDiscountAmount))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {actualBonusToUse > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Списано бонусов
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{formatNumber(Math.round(actualBonusToUse))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        px={2}
                                        py={2}
                                        sx={{
                                            borderTop:
                                                actualBonusToUse > 0 || promoDiscountAmount > 0 || totalSavings > 0
                                                    ? '1px solid #E3ECF3'
                                                    : 'none',
                                        }}
                                    >
                                        <Typography
                                            color="primary"
                                            variant="h6"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 20,
                                            }}
                                        >
                                            Итого
                                        </Typography>
                                        <Typography
                                            color="primary"
                                            variant="h6"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 20,
                                            }}
                                        >
                                            {formatNumber(Math.round(finalTotal))} ₽
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    disabled={
                                        isSubmitting ||
                                        Array.from(selectedCategories.values()).some((s) => !s.packageSize)
                                    }
                                >
                                    {isSubmitting ? (
                                        <>
                                            <CircularProgress
                                                size={20}
                                                sx={{ mr: 1 }}
                                            />
                                            Обработка...
                                        </>
                                    ) : (
                                        'Перейти к оплате'
                                    )}
                                </Button>
                            </Box>
                        </>
                    )}
                </form>
            </Box>
        );
    };

    if (!isMobile) {
        return <DesktopLayout>{renderContent()}</DesktopLayout>;
    }

    return renderContent();
}
